import { Clock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { cn } from '~/lib/utils'
import { Button } from './button'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { ScrollArea } from './scroll-area'
import { Separator } from './separator'

interface TimePickerProps {
  id?: string
  name?: string
  value?: string
  minutesStep?: number
  className?: string
  ariaInvalid?: boolean
  onChange?: (value: string) => void
  disabled?: boolean
}

export const TimePicker = ({
  id,
  value,
  minutesStep = 1,
  className,
  onChange,
  ariaInvalid,
  disabled = false,
  ...props
}: TimePickerProps) => {
  const { time, setHours, setMinutes } = useTime(value ?? '')
  const [hours, minutes] = (time || '--:--').split(':')

  const hoursItems = Array.from({ length: 24 }, (_, i) => i)
  const minutesItems = Array.from({ length: 60 }, (_, i) => i).filter((i) => i % minutesStep === 0)

  function handleChange(newTime: string) {
    onChange?.(newTime)
  }

  return (
    <Popover onOpenChange={(open) => (open ? undefined : handleChange(time))}>
      <PopoverTrigger
        id={id}
        className={cn('group w-full rounded-lg', className)}
        render={<Button variant="outline" />}
        aria-invalid={ariaInvalid}
        disabled={disabled}
        {...props}
      >
        {hours} : {minutes}
        <Clock className="ml-auto group-hover:text-foreground size-4 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent className="w-fit p-0 flex flex-row gap-0" align="start">
        <TimePickerColumn
          id={`${id}-hours`}
          items={hoursItems}
          selected={Number(hours)}
          onSelect={(v) => setHours(v.toString().padStart(2, '0'))}
        />
        <Separator orientation="vertical" />
        <TimePickerColumn
          id={`${id}-minutes`}
          items={minutesItems}
          selected={Number(minutes)}
          onSelect={(v) => setMinutes(v.toString().padStart(2, '0'))}
        />
      </PopoverContent>
    </Popover>
  )
}

function TimePickerColumn({
  id,
  items,
  onSelect,
  selected,
}: {
  id: string
  items: number[]
  onSelect: (item: number) => void
  selected: number
}) {
  return (
    <ScrollArea
      className="h-60 w-12"
      containerClassName="flex flex-col p-1"
      scrollBarClassName="hidden"
      scrollbarGutter={false}
    >
      {items.map((item) => (
        <Button
          key={`${id}-${item}`}
          ref={(el) => {
            if (el && selected === item) {
              requestAnimationFrame(() => {
                el.scrollIntoView({ block: 'nearest', behavior: 'auto' })
              })
            }
          }}
          variant="ghost"
          className={cn(
            'rounded-lg w-full',
            selected === item && 'bg-primary text-primary-foreground',
          )}
          onClick={() => onSelect(item)}
        >
          {item}
        </Button>
      ))}
    </ScrollArea>
  )
}

function useTime(value: string) {
  const [time, setTime] = useState(value)
  const splitTime = time.split(':')
  const hours = splitTime[0] ?? '00'
  const minutes = splitTime[1] ?? '00'

  useEffect(() => {
    setTime(value)
  }, [value])

  function setHours(newHours: string) {
    setTime(`${newHours}:${minutes}`)
  }

  function setMinutes(newMinutes: string) {
    setTime(`${hours}:${newMinutes}`)
  }

  return { time, setHours, setMinutes, hours, minutes }
}
